/* eslint-disable no-param-reassign  */
import { createSlice } from '@reduxjs/toolkit';
import { isBlank, isPresent } from 'utils/helperMethods';
import { reject, findIndex, propEq, omit, uniq, find, append, isNil, pathOr } from 'ramda';
import { useDispatch } from 'react-redux';
import { camelize } from 'utils/keysConverter';
import { firebaseAuth, firebaseServerValue } from 'services/firebase/app';
import { FETCH_STATUSES } from 'utils/fetchStatusUtils';
import {
  push,
  update,
  setOnDisconnect,
  loadAndSubscribeToList,
  set,
  loadAndSubscribeToListWithOrder,
  offConnection,
} from 'services/firebase/utils';
import { firebaseRoutes } from 'routes';
import MessagePresenter from 'presenters/MessagePresenter';
import ChatsPresenter from 'presenters/ChatsPresenter';

import SignedUrlRepository from 'repositories/SignedUrlRepository';
import FirebaseTokenRepository from 'repositories/FirebaseTokenRepository';

const additionalPropsForVideoMessage = {
  videoLoadingStatus: FETCH_STATUSES.idle,
  videoUrl: '',
};

const initialState = {
  initialized: false,
  firebaseAuthStatus: FETCH_STATUSES.idle,
  isOnline: false,
  onlineStatusSettingStatus: FETCH_STATUSES.idle,
  chats: [],
  chatsLoadingStatus: FETCH_STATUSES.idle,
  messages: {},
  messagesLoadingStatus: FETCH_STATUSES.idle,
  usersOnlineStatusesLoadingStatus: FETCH_STATUSES.idle,
  subscribedChatMessagesUids: [],
  usersOnlineStatusList: {},
  needToLoadChatsInfo: false,
  needToSelectChat: false,
  selectedChatUid: null,
};

const firebaseChatSlice = createSlice({
  name: 'firebaseChatSlice',
  initialState,
  reducers: {
    setInitialized(state, { payload }) {
      const { initialized } = payload;
      state.initialized = initialized;
    },
    authFirebaseStart(state) {
      state.firebaseAuthStatus = FETCH_STATUSES.pending;
    },
    authFirebaseFinish(state) {
      state.firebaseAuthStatus = FETCH_STATUSES.fulfilled;
    },
    authFirebaseError(state) {
      state.firebaseAuthStatus = FETCH_STATUSES.failed;
    },
    setUserOnlineStatusStart(state) {
      state.onlineStatusSettingStatus = FETCH_STATUSES.pending;
    },
    setUserOnlineStatusSuccess(state) {
      state.onlineStatusSettingStatus = FETCH_STATUSES.fulfilled;
      state.isOnline = true;
    },
    setUserOnlineStatusFail(state) {
      state.onlineStatusSettingStatus = FETCH_STATUSES.failed;
    },
    resetFirebase() {
      return { ...initialState };
    },
    loadChatsStart(state) {
      state.chatsLoadingStatus = FETCH_STATUSES.pending;
    },
    loadChatsSuccess(state, { payload }) {
      state.chats = payload.item.map(({ key, item }) => ({ uid: key, ...item }));
      state.needToSelectChat = true;
      state.chatsLoadingStatus = FETCH_STATUSES.fulfilled;
    },
    createChat(state, { payload }) {
      const { item, uid } = payload;
      const existingChat = find(propEq('uid', uid))(state.chats);
      if (existingChat) return;

      const chat = omit(['key'], camelize(item));
      state.chats = [{ ...chat, uid }, ...state.chats];
      state.needToLoadChatsInfo = true;
    },
    updateChat(state, { payload }) {
      const { item, uid } = payload;
      const chat = omit(['key'], camelize(item));
      const chatIndex = findIndex(propEq('uid', uid))(state.chats);
      const { chats } = state;
      chats[chatIndex] = { ...chat, uid };
      const sortedChats = ChatsPresenter.sortedByNewestMessage(chats);
      state.chats = sortedChats;
    },
    deleteChat(state, { payload }) {
      const { uid } = payload;
      state.chats = reject(propEq('uid', uid), state.chats);
    },
    setSelectedChatUid(state, { payload }) {
      const { value } = payload;
      state.selectedChatUid = value;
    },
    loadMessagesSuccess(state, { payload }) {
      const { item, chatUid } = payload;
      state.messagesLoadingStatus = FETCH_STATUSES.fulfilled;
      if (isPresent(item)) {
        const messages = Object.entries(item).map(([key, value]) => {
          const isVideoMessage = MessagePresenter.isVideoMessage(value);
          if (isVideoMessage) {
            return { uid: key, ...camelize(value), chatUid, ...additionalPropsForVideoMessage };
          }
          return { uid: key, chatUid, ...camelize(value) };
        });
        state.messages[chatUid] = messages;
      }
      state.subscribedChatMessagesUids = uniq([...state.subscribedChatMessagesUids, chatUid]);
    },
    loadMessagesStart(state) {
      state.messagesLoadingStatus = FETCH_STATUSES.pending;
    },
    createMessage(state, { payload }) {
      const { item, uid, chatUid } = payload;
      const messages = pathOr([], [chatUid], state.messages);
      const message = omit(['key'], camelize(item));
      const messageIndex = findIndex(propEq('uid', uid))(messages);
      if (messageIndex === -1) {
        const preparedMessage = MessagePresenter.isVideoMessage(message)
          ? { uid, ...message, chatUid, ...additionalPropsForVideoMessage }
          : { uid, ...message, chatUid };
        state.messages[chatUid] = append(preparedMessage, messages);
      }
    },
    updateMessage(state, { payload }) {
      const { item, uid, chatUid } = payload;
      const message = omit(['key'], camelize(item));
      const messages = state.messages[chatUid];
      const preparedMessage = MessagePresenter.isVideoMessage(message)
        ? { uid, ...message, chatUid, ...additionalPropsForVideoMessage }
        : { uid, ...message, chatUid };
      if (isBlank(messages)) {
        state.messages[chatUid] = append(preparedMessage, messages);
        return;
      }
      const messageIndex = findIndex(propEq('uid', uid))(messages);
      if (messageIndex !== -1) {
        state.messages[chatUid][messageIndex] = preparedMessage;
        return;
      }
      state.messages[chatUid] = append(preparedMessage, messages);
    },
    getSignedUrlStart(state, { payload }) {
      const { chatUid, messageUid } = payload;
      const messages = state.messages[chatUid];
      const message = find(propEq('uid', messageUid), messages);
      message.videoLoadingStatus = FETCH_STATUSES.pending;
    },
    getSignedUrlSuccess(state, { payload }) {
      const { chatUid, messageUid, url } = payload;
      const messages = state.messages[chatUid];
      const message = find(propEq('uid', messageUid), messages);
      message.videoLoadingStatus = FETCH_STATUSES.fulfilled;
      message.videoUrl = url;
    },
    getSignedUrlFail(state, { payload }) {
      const { chatUid, messageUid } = payload;
      const messages = state.messages[chatUid];
      const message = find(propEq('uid', messageUid), messages);
      message.videoLoadingStatus = FETCH_STATUSES.failed;
    },
    updateChatParticipantOnlineStatus(state, { payload }) {
      const { item, userId } = payload;
      state.usersOnlineStatusList[userId] = !!item;
    },
    loadUsersOnlineStatusesStart(state) {
      state.usersOnlineStatusesLoadingStatus = FETCH_STATUSES.pending;
    },
    loadUsersOnlineStatusesSuccess(state, { payload }) {
      const { item } = payload;
      state.usersOnlineStatusesLoadingStatus = FETCH_STATUSES.fulfilled;
      state.usersOnlineStatusList = item;
    },
    addUserOnlineStatus(state, { payload }) {
      const { item, id } = payload;
      const userStatus = omit(['key'], item);
      state.usersOnlineStatusList[id] = userStatus;
    },
    updateUserOnlineStatus(state, { payload }) {
      const { item, id } = payload;
      const userStatus = omit(['key'], item);
      state.usersOnlineStatusList[id] = userStatus;
    },
    loadedChatsInfo(state) {
      state.needToLoadChatsInfo = false;
    },
    readMessages(state, { payload }) {
      const { chatUid } = payload;
      const { chats } = state;
      const chat = find(propEq('uid', chatUid))(chats);
      const chatIndex = findIndex(propEq('uid', chatUid))(chats);
      chats[chatIndex] = { ...chat, unreadCount: 0 };
    },
    setNeedToSelectChat(state, { payload }) {
      const { value } = payload;
      state.needToSelectChat = value;
    },
  },
});

export const { actions } = firebaseChatSlice;

export default firebaseChatSlice.reducer;

export const useAppActions = () => {
  const dispatch = useDispatch();

  const loadAndSubscribeToChats = currentUserId => {
    const path = firebaseRoutes.chatsPath(currentUserId);
    const orderPath = firebaseRoutes.chatOrderChildPath(currentUserId);
    dispatch(actions.loadChatsStart());

    const loadAndSubscribeActions = {
      load: ({ item }) => {
        dispatch(actions.loadChatsSuccess({ item }));
      },
      update: ({ item, key }) => {
        dispatch(actions.updateChat({ item, uid: key }));
      },
      create: ({ item, key }) => {
        dispatch(actions.createChat({ item, uid: key }));
      },
      delete: ({ key }) => {
        dispatch(actions.deleteChat({ uid: key }));
      },
    };

    loadAndSubscribeToListWithOrder(path, loadAndSubscribeActions, orderPath);
  };

  const loadAndSubscribeToMessages = chatUid => {
    const path = firebaseRoutes.messagesPath(chatUid);
    dispatch(actions.loadMessagesStart());

    const loadAndSubscribeActions = {
      load: ({ item }) => {
        dispatch(actions.loadMessagesSuccess({ item, chatUid }));
      },
      create: ({ item, key }) => {
        dispatch(actions.createMessage({ item, uid: key, chatUid }));
      },
      update: ({ item, key }) => {
        dispatch(actions.updateMessage({ item, uid: key, chatUid }));
      },
    };

    loadAndSubscribeToList(path, loadAndSubscribeActions);
  };

  const readMessages = (userId, chatUid) => {
    const path = firebaseRoutes.chatUnreadCountPath(userId, chatUid);
    dispatch(actions.readMessages({ chatUid }));

    set(path, 0);
  };

  const sendMessage = async (participantId, currentUserId, chatUid, message, dayOfYear = null) => {
    const messagesPath = firebaseRoutes.chatMessagesPath(chatUid);
    const unreadCountPath = firebaseRoutes.chatUnreadCountPath(participantId, chatUid);

    const emptyMessage = { type: 'empty' };
    const messageUid = await push(messagesPath, emptyMessage);

    const conversationDaysPath = firebaseRoutes.conversationDaysPath(currentUserId, chatUid, messageUid);

    const messagePath = firebaseRoutes.chatMessagePath(chatUid, messageUid);
    const userLatesMessagePath = firebaseRoutes.latestMessagePath(currentUserId, chatUid);
    const participantLatestMessagePath = firebaseRoutes.latestMessagePath(participantId, chatUid);

    dispatch(actions.updateMessage({ message, uid: messageUid, chatUid }));

    const basicUpdates = {
      [messagePath]: { ...message, createdAt: firebaseServerValue.TIMESTAMP },
      [unreadCountPath]: firebaseServerValue.increment(1),
      [userLatesMessagePath]: { ...message, createdAt: firebaseServerValue.TIMESTAMP },
      [participantLatestMessagePath]: { ...message, createdAt: firebaseServerValue.TIMESTAMP },
    };

    const updates = isNil(dayOfYear) ? basicUpdates : { ...basicUpdates, [conversationDaysPath]: dayOfYear };

    update(updates);
  };

  const getSignedUrl = async (chatUid, messageUid, params) => {
    dispatch(actions.getSignedUrlStart({ chatUid, messageUid }));
    try {
      const { url } = await SignedUrlRepository.get(params);
      dispatch(actions.getSignedUrlSuccess({ chatUid, messageUid, url }));
    } catch (_error) {
      dispatch(actions.getSignedUrlFail({ chatUid, messageUid }));
    }
  };

  const subscribeToUsersOnlineStatuses = () => {
    const path = firebaseRoutes.usersOnlineStatusesPath();
    dispatch(actions.loadUsersOnlineStatusesStart());

    const subscribeToUserOnlineStatusActions = {
      load: ({ item }) => {
        dispatch(actions.loadUsersOnlineStatusesSuccess({ item }));
      },
      create: ({ item, key }) => {
        dispatch(actions.addUserOnlineStatus({ item, id: key }));
      },
      update: ({ item, key }) => {
        dispatch(actions.updateUserOnlineStatus({ item, id: key }));
      },
    };

    loadAndSubscribeToList(path, subscribeToUserOnlineStatusActions);
  };

  const setChatFavorite = (state, userId, chatUid) => {
    const path = firebaseRoutes.chatFavoritePath(userId, chatUid);
    set(path, state);
  };

  const setChatHidden = (state, userId, chatUid) => {
    const path = firebaseRoutes.chatHiddenPath(userId, chatUid);
    set(path, state);
  };

  const loadedChatsInfo = () => {
    dispatch(actions.loadedChatsInfo());
  };

  const changeSelectedChatUid = value => {
    dispatch(actions.setSelectedChatUid({ value }));
  };

  const setNeedToSelectChat = value => {
    dispatch(actions.setNeedToSelectChat({ value }));
  };

  const authFirebase = async () => {
    dispatch(actions.authFirebaseStart());
    try {
      const { token } = await FirebaseTokenRepository.show();
      await firebaseAuth.signInWithCustomToken(token);
      dispatch(actions.authFirebaseFinish());
    } catch (_error) {
      dispatch(actions.authFirebaseError());
    }
  };

  const setUserOnlineStatus = async id => {
    dispatch(actions.setUserOnlineStatusStart());
    try {
      const path = firebaseRoutes.userOnlineStatusPath(id);

      await set(path, true);
      dispatch(actions.setUserOnlineStatusSuccess());
    } catch (_error) {
      dispatch(actions.setUserOnlineStatusFail());
    }
  };

  const setOfflineStatusOnDisconnect = async id => {
    const path = firebaseRoutes.userOnlineStatusPath(id);

    setOnDisconnect(path, false);
  };

  const resetFirebase = (currentUserId, chats) => {
    dispatch(actions.resetFirebase());
    const onlineStatusesPath = firebaseRoutes.usersOnlineStatusesPath();
    const chatsPath = firebaseRoutes.chatsPath(currentUserId);
    const onlineStatusPath = firebaseRoutes.userOnlineStatusPath(currentUserId);

    set(onlineStatusPath, false);
    offConnection(onlineStatusesPath);
    offConnection(chatsPath);
    chats.forEach(({ uid }) => {
      const messagesPath = firebaseRoutes.messagesPath(uid);
      offConnection(messagesPath);
    });
  };

  const setInitialized = initialized => {
    dispatch(actions.setInitialized({ initialized }));
  };

  return {
    loadAndSubscribeToChats,
    setNeedToSelectChat,
    changeSelectedChatUid,
    setChatFavorite,
    setChatHidden,
    subscribeToUsersOnlineStatuses,
    readMessages,
    loadAndSubscribeToMessages,
    getSignedUrl,
    sendMessage,
    loadedChatsInfo,
    authFirebase,
    setUserOnlineStatus,
    setOfflineStatusOnDisconnect,
    resetFirebase,
    setInitialized,
  };
};
/* eslint-enable no-param-reassign */



// WEBPACK FOOTER //
// ./src/reducers/slices/firebase/chatSlice.js